import { useEffect, useRef, useState } from 'react'
import { useInView, useReducedMotion } from 'motion/react'
import { Check } from 'lucide-react'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import type { IndustryData } from '@/data/industries'
import { cn } from '@/lib/utils'
import { APP_ICON } from './listing'

const ROW_MS = 1100

/** "Technical Specification" → "TS". */
const code = (name: string) =>
  name
    .replace(/[^A-Za-z ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('')

/**
 * A document register drawn in JSX (the engineering hero's product
 * fragment): one row per template with a document number, app, revision
 * and status. While in view a review pass walks down the rows: the
 * reviewing agent's band moves row by row and each status flips from
 * "In review" to "Checked". Paused off-screen; fully checked and still
 * under reduced motion. Decorative.
 */
export function DrawingRegister({ industry, className }: { industry: IndustryData; className?: string }) {
  const rows = industry.templates
  const reviewer =
    industry.aiAgents.find((a) => /quality|review/i.test(a.role)) ?? industry.aiAgents[0]
  const writer = industry.aiAgents.find((a) => /writer/i.test(a.role)) ?? industry.aiAgents[0]
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.3 })
  const reduce = useReducedMotion()
  // Index of the row under review; rows above it are checked. rows.length = all done (pause).
  const [cursor, setCursor] = useState(0)

  useEffect(() => {
    if (reduce || !inView) return
    const id = window.setInterval(() => setCursor((c) => (c >= rows.length + 1 ? 0 : c + 1)), ROW_MS)
    return () => window.clearInterval(id)
  }, [inView, reduce, rows.length])

  const at = reduce ? rows.length : cursor

  return (
    <div
      ref={ref}
      aria-hidden
      className={cn(
        'relative overflow-hidden border border-[var(--color-landing-gray-900)] bg-white text-[var(--color-landing-gray-900)] dark:border-white/70 dark:bg-[#12121a] dark:text-white',
        className
      )}
    >
      {/* Title block, as on a drawing sheet */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-[var(--color-landing-gray-900)] px-4 py-3 dark:border-white/70 md:px-6">
        <span className="font-[family-name:var(--font-landing-display)] text-sm uppercase leading-[1.4] md:text-base">
          Document register
        </span>
        <span className="flex items-center gap-4 font-[family-name:var(--font-landing-mono)] text-[11px] uppercase tracking-[0.04em] text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
          <span>Drafted: {writer?.name}</span>
          <span>Checked: {reviewer?.name}</span>
          <span className="hidden sm:inline">
            {Math.min(at, rows.length)} / {rows.length}
          </span>
        </span>
      </div>

      <div className="relative [--row:2.5rem] md:[--row:2.75rem]">
        {/* Review band: moves row by row (transform only) */}
        {!reduce && at < rows.length && (
          <span
            className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[var(--row)] bg-[#ff00f2]/15 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateY(calc(var(--row) * ${at + 1}))` }}
          />
        )}
        <table className="relative z-10 w-full table-fixed text-left text-[12px] md:text-[13px]">
          <thead>
            <tr className="h-[var(--row)] border-b border-[var(--color-landing-gray-300)] font-[family-name:var(--font-landing-mono)] text-[10px] uppercase tracking-[0.06em] text-[var(--color-landing-gray-500)] dark:border-[var(--color-landing-gray-700)] md:text-[11px]">
              <th className="w-[5.5rem] px-4 font-medium md:w-[7rem] md:px-6">Doc no.</th>
              <th className="px-2 font-medium">Title</th>
              <th className="hidden px-2 font-medium lg:table-cell">Scope</th>
              <th className="hidden w-[5rem] px-2 font-medium sm:table-cell">App</th>
              <th className="hidden w-[4rem] px-2 font-medium md:table-cell">Rev</th>
              <th className="w-[7.5rem] px-4 font-medium md:w-[9rem] md:px-6">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t, i) => {
              const checked = i < at
              const reviewing = i === at
              const app = APP_ICON[t.type]
              return (
                <tr key={t.name} className="h-[var(--row)] border-b border-[var(--color-landing-gray-200)] last:border-b-0 dark:border-[var(--color-landing-gray-800)]">
                  <td className="px-4 font-[family-name:var(--font-landing-mono)] tabular-nums md:px-6">
                    {code(t.name)}-{String(i + 1).padStart(3, '0')}
                  </td>
                  <td className="truncate px-2 font-semibold">{t.name}</td>
                  <td className="hidden truncate px-2 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)] lg:table-cell">
                    {t.description}
                  </td>
                  <td className="hidden px-2 sm:table-cell">
                    <span className="flex items-center gap-1.5">
                      {app && <OfficeAppIcon app={app} className="h-4 w-4" />}
                      {t.type}
                    </span>
                  </td>
                  <td className="hidden px-2 font-[family-name:var(--font-landing-mono)] md:table-cell">{checked ? 'B' : 'A'}</td>
                  <td className="px-4 md:px-6">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] transition-colors duration-300 md:text-[11px]',
                        checked
                          ? 'bg-[#00d722] text-[#0a0a0a]'
                          : reviewing
                            ? 'bg-[#ff00f2] text-[#0a0a0a]'
                            : 'bg-[var(--color-landing-gray-100)] text-[var(--color-landing-gray-600)] dark:bg-[#22222c] dark:text-[var(--color-landing-gray-300)]'
                      )}
                    >
                      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                      {checked ? 'Checked' : reviewing ? 'In review' : 'Draft'}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
